// HR people records (UI-3E): plain words and server-mirroring rules for Employees, Employee Detail and Separation.
// The server decides every action; these helpers only keep the screen from offering what it would refuse.

export const memberStatus = (member) => String(member?.status || "").toUpperCase();
export const isFormerMember = (member) => ["TERMINATED", "LEFT"].includes(memberStatus(member));
export const isOwnerMember = (member) => String(member?.role || "").toUpperCase() === "OWNER";
export const isInvitation = (member) => String(member?.id || "").startsWith("inv-") || memberStatus(member) === "INVITED";

/**
 * Whether the business keeps an employment record for this member (APP-HR-109). The record is its own row on the
 * server, created when someone is hired or their employment is first saved; `recordUpdatedAt` is present exactly when
 * that row exists. A start date or an employee code can only come from that record, so either also proves it.
 * The role does not decide this: an owner with an employment record is an employee, one without is not.
 */
export const hasEmploymentRecord = (member) => Boolean(member?.recordUpdatedAt || member?.hireDate || member?.employeeCode);

/**
 * Who belongs in HR › Employees: people with an employment record, people on their way to one (an invitation, or
 * still onboarding) and former employees. A business member with none of these (typically the owner) has access to
 * the business but is not an employee.
 */
export const isEmployeeRecord = (member) => hasEmploymentRecord(member) || isInvitation(member)
  || memberStatus(member) === "ON_HOLD" || isFormerMember(member);

const STATUS = {
  ACTIVE: { label: "Active", tone: "neutral" },
  ON_HOLD: { label: "Onboarding", tone: "neutral" },
  INVITED: { label: "Invited", tone: "neutral" },
  SUSPENDED: { label: "Suspended", tone: "warning" },
  TERMINATED: { label: "Former employee", tone: "neutral" },
  LEFT: { label: "Left before starting", tone: "neutral" },
};

/** The employment status as a word, never the stored code. */
export function statusWord(member) {
  return STATUS[memberStatus(member)] || { label: "Unknown", tone: "neutral" };
}

export const STATUS_FILTERS = [
  { value: "current", label: "Current employees" },
  { value: "ACTIVE", label: "Active" },
  { value: "SUSPENDED", label: "Suspended" },
  { value: "ON_HOLD", label: "Onboarding" },
  { value: "INVITED", label: "Invited" },
  { value: "former", label: "Former employees" },
  { value: "all", label: "Everyone" },
];

export function matchesStatusFilter(member, filter) {
  const status = memberStatus(member);
  if (filter === "all") return true;
  if (filter === "current") return !isFormerMember(member);
  if (filter === "former") return isFormerMember(member);
  return status === filter;
}

export const roleName = (member) => member?.roleDisplayName || member?.role_display_name || member?.role || "";

/** Role filter options from the roles people actually hold in this business, by display name. */
export function roleOptions(members) {
  const names = new Map();
  for (const member of members || []) {
    const key = String(member?.role || "");
    if (key && !names.has(key)) names.set(key, roleName(member));
  }
  return [...names.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function departmentOptions(members) {
  const names = new Set((members || []).map((member) => member?.department).filter(Boolean));
  return [...names].sort((a, b) => a.localeCompare(b));
}

export function filterMembers(members, { status = "current", role = "", department = "", query = "" } = {}) {
  const q = query.trim().toLowerCase();
  return (members || [])
    .filter((member) => matchesStatusFilter(member, status))
    .filter((member) => !role || String(member.role || "") === role)
    .filter((member) => !department || (member.department || "") === department)
    .filter((member) => !q || [member.name, member.email, member.employeeCode, roleName(member), member.department]
      .filter(Boolean).join(" ").toLowerCase().includes(q))
    .sort((a, b) => Number(isFormerMember(a)) - Number(isFormerMember(b)) || String(a.name || "").localeCompare(String(b.name || "")));
}

/**
 * Lifecycle actions the server would accept from this viewer (business-members.service / separation.service):
 * suspend — owner only, target active and past onboarding;
 * reinstate — owner only, target suspended; separate — owner only, target active or suspended, nothing scheduled.
 */
export function lifecycleActions({ viewerRole, member, preActivation, scheduledSeparation }) {
  const owner = String(viewerRole || "").toUpperCase() === "OWNER";
  const status = memberStatus(member);
  const eligible = member && !isOwnerMember(member) && !isInvitation(member) && !isFormerMember(member);
  return {
    suspend: Boolean(eligible && owner && status === "ACTIVE" && !preActivation),
    reinstate: Boolean(eligible && owner && status === "SUSPENDED"),
    separate: Boolean(eligible && owner && ["ACTIVE", "SUSPENDED"].includes(status) && !preActivation && !scheduledSeparation),
    endOnboarding: Boolean(eligible && owner && status === "ON_HOLD"),
  };
}

const STRUCTURE_FIELDS = [
  { key: "businessRoleId", label: "Role and access", consequential: true },
  { key: "departmentId", label: "Department", consequential: true },
  { key: "supervisorMembershipId", label: "Supervisor", consequential: true },
  { key: "employmentType", label: "Employment type", consequential: true },
  { key: "hireDate", label: "Start date", consequential: true },
  { key: "contactNumber", label: "Contact number", consequential: false },
  { key: "employmentEmail", label: "Work email", consequential: false },
  { key: "remarks", label: "Remarks", consequential: false },
];

/**
 * What an employment-record edit changes, field by field. `describe(key, value)` turns ids into names.
 * A change to role, department, supervisor, type or start date is reviewed before saving (access, payroll
 * coverage and reporting lines follow it); contact details and remarks save directly.
 */
export function structureChanges(before, after, describe = (key, value) => value) {
  return STRUCTURE_FIELDS
    .filter(({ key }) => String(before?.[key] ?? "") !== String(after?.[key] ?? ""))
    .map(({ key, label, consequential }) => ({
      key, label, consequential,
      from: describe(key, before?.[key]) || "Not set",
      to: describe(key, after?.[key]) || "Not set",
    }));
}

export const EMPLOYEE_SECTIONS = [
  { key: "", label: "Overview" },
  { key: "attendance", label: "Attendance" },
  { key: "leave", label: "Leave" },
  { key: "pay", label: "Pay" },
  { key: "government", label: "Government & tax" },
  { key: "payslips", label: "Payslips" },
  { key: "deductions", label: "Deductions" },
  { key: "financials", label: "Financial records" },
  { key: "history", label: "History" },
  { key: "activity", label: "Activity" },
];

/** Sections this viewer may read; payroll and pay data are explicit permissions (owner decision C5). */
export function visibleSections(can) {
  return EMPLOYEE_SECTIONS.filter(({ key }) => {
    if (key === "activity") return can.activity;
    // Pay holds compensation and, for payroll managers, how holiday pay is handled.
    if (key === "pay") return can.compensation || can.payroll;
    if (key === "government") return can.government || can.compensation;
    if (key === "payslips" || key === "financials") return can.payroll;
    if (key === "leave") return can.leave;
    return true;
  });
}

/** Employment-history event types as words. */
const EVENT_WORDS = {
  hired: "Hired", joined: "Joined", rehired: "Rehired", activated: "Started work", suspended: "Suspended",
  reinstated: "Reinstated", restored_to_on_hold: "Returned to onboarding", terminated: "Employment ended",
  left: "Left before starting", role_changed: "Role changed", profile_updated: "Employment record updated",
};

export function eventWord(type) {
  const key = String(type || "").toLowerCase();
  if (EVENT_WORDS[key]) return EVENT_WORDS[key];
  const text = key.replace(/_/g, " ").trim();
  return text ? text[0].toUpperCase() + text.slice(1) : "Change";
}
